import { Presence } from "@liveblocks/client";
import { useUpdateMyPresence } from "@liveblocks/react";
import { useAtom } from "jotai";
import { getStroke } from "perfect-freehand";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSendAnalyticsEvent } from "../analytics/events";
import { BORDER_WIDTH } from "../blocks/Block";
import createBlock from "../blocks/createBlock";
import { Button } from "../components/Button";
import {
  DEFAULT_GRID_RESOLUTION,
  DEFAULT_SPACE_SIZE_PIXELS,
  GRID_SEAM,
  SIDE_NAV_WIDTH,
} from "../consts";
import { colorValue } from "../css/color";
import {
  blocksOpsRefAtom,
  currentUserAtom,
  sideNavOnAtom,
} from "../jotai/atoms";
import { useUserInfo, useUserInfos } from "../liveblocks/maps";
import { usePresence } from "../liveblocks/presences";
import { styled } from "../stitches.config";
import { DrawingInputPath, DrawingInputPaths } from "../types";
import { canvasPos } from "../utils/canvasPos";
import {
  clampPosition,
  pixelToGridPosition,
  pixelToGridSize,
} from "../utils/gridPosition";
import useKeyAction from "../utils/useKeyAction";

const STROKE_SIZE = 6;

function getSvgPathFromStroke(stroke: number[][]) {
  if (!stroke.length) return "";
  const d = stroke.reduce(
    (acc, [x0, y0], i, arr) => {
      const [x1, y1] = arr[(i + 1) % arr.length];
      acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
      return acc;
    },
    ["M", ...stroke[0], "Q"]
  );
  d.push("Z");
  return d.join(" ");
}

function ActiveSvg({
  drawingInputPaths,
  fill,
  style,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: {
  drawingInputPaths: DrawingInputPaths;
  fill: string;
  style?: React.CSSProperties;
  onPointerDown?: (e: React.PointerEvent) => void;
  onPointerMove?: (e: React.PointerEvent) => void;
  onPointerUp?: (e: React.PointerEvent) => void;
}) {
  const [navOn] = useAtom(sideNavOnAtom);

  return (
    <ActiveSvgElement
      style={{
        ...style,
        left: navOn ? SIDE_NAV_WIDTH : 0,
      }}
      data-cursor={"pen"}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {drawingInputPaths.map((drawingInputPath: DrawingInputPath) => {
        const stroke = getStroke(drawingInputPath, {
          size: STROKE_SIZE,
          simulatePressure: false,
        });
        const pathData = getSvgPathFromStroke(stroke);
        return (
          <path key={drawingInputPath.join("-")} d={pathData} fill={fill} />
        );
      })}
    </ActiveSvgElement>
  );
}

function OtherDrawMode({
  drawingInputPaths,
  userId,
}: {
  drawingInputPaths: DrawingInputPaths;
  userId: string;
}) {
  const [userInfo] = useUserInfo(userId);
  const fill = userInfo?.color ? colorValue(userInfo.color, 60) : "black";

  return (
    <ActiveSvg
      drawingInputPaths={drawingInputPaths}
      fill={fill}
      style={{
        pointerEvents: "none",
      }}
    />
  );
}

function SelfDrawMode() {
  const [currentUser] = useAtom(currentUserAtom);
  const [userInfo] = useUserInfo(currentUser?.id);
  const updateMyPresence = useUpdateMyPresence();
  const [drawingInputPaths, setDrawingInputPaths] = useState<DrawingInputPaths>(
    []
  );

  useEffect(() => {
    updateMyPresence({ drawMode: drawingInputPaths });
  }, [drawingInputPaths, updateMyPresence]);

  function handlePointerDown(e: React.PointerEvent<Element>) {
    const { x, y } = canvasPos(e);
    setDrawingInputPaths([...drawingInputPaths, [[x, y]]]);
    e.stopPropagation();
    if (e.target instanceof Element) {
      e.target.setPointerCapture(e.pointerId);
    }
  }

  function handlePointerMove(e: React.PointerEvent<Element>) {
    const { x, y } = canvasPos(e);
    if (e.buttons !== 1) return;
    const currentInputPath = drawingInputPaths[drawingInputPaths.length - 1];
    setDrawingInputPaths([
      ...drawingInputPaths.slice(0, drawingInputPaths.length - 1),
      [...currentInputPath, [x, y]],
    ]);
  }

  function handlePointerUp(e: React.PointerEvent<Element>) {
    if (e.target instanceof Element) {
      e.target.releasePointerCapture(e.pointerId);
    }
  }

  const fill = userInfo?.color ? colorValue(userInfo.color, 60) : "black";
  return (
    <ActiveSvg
      drawingInputPaths={drawingInputPaths}
      fill={fill}
      style={{
        cursor: "none",
        touchAction: "none",
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    />
  );
}

function DrawModeStatus({
  myDrawMode,
}: {
  myDrawMode: DrawingInputPaths | null;
}) {
  const [currentUser] = useAtom(currentUserAtom);
  const [userInfos] = useUserInfos();
  const updateMyPresence = useUpdateMyPresence();
  const [blocksOpsRef] = useAtom(blocksOpsRefAtom);
  const drawingInputPathsRef = useRef<DrawingInputPaths>(myDrawMode || []);
  drawingInputPathsRef.current = myDrawMode || [];
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const createImage = useCallback(() => {
    const drawingInputPaths = drawingInputPathsRef.current;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const drawingInputPath of drawingInputPaths) {
      for (const [x, y] of drawingInputPath) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }

    const padding = 12;
    const inset = BORDER_WIDTH + GRID_SEAM / 2;
    const fill = currentUser
      ? colorValue(userInfos[currentUser?.id]?.color, 60) || "black"
      : "black";
    minX =
      Math.floor((minX - padding) / DEFAULT_GRID_RESOLUTION) *
      DEFAULT_GRID_RESOLUTION;
    minY =
      Math.floor((minY - padding) / DEFAULT_GRID_RESOLUTION) *
      DEFAULT_GRID_RESOLUTION;
    maxX =
      Math.ceil((maxX + padding) / DEFAULT_GRID_RESOLUTION) *
      DEFAULT_GRID_RESOLUTION;
    maxY =
      Math.ceil((maxY + padding) / DEFAULT_GRID_RESOLUTION) *
      DEFAULT_GRID_RESOLUTION;

    const pathData = drawingInputPaths.map((line: number[][]) => {
      const adjustedLine = line.map(([x, y, p]) => [x - minX, y - minY, p]);
      const stroke = getStroke(adjustedLine, {
        size: STROKE_SIZE,
        simulatePressure: false,
      });
      const path = getSvgPathFromStroke(stroke);
      return { d: path, fill: fill };
    });

    const newBlock = createBlock(
      "svg",
      clampPosition(pixelToGridPosition({ x: minX, y: minY }, Math.round)),
      pixelToGridSize(
        {
          width: maxX - minX,
          height: maxY - minY,
        },
        Math.round
      ),
      JSON.stringify({
        viewBox: [
          inset,
          inset,
          maxX - minX - inset * 2,
          maxY - minY - inset * 2,
        ],
        paths: pathData,
      })
    );

    if (blocksOpsRef.current.upsertBlock && newBlock) {
      sendAnalyticsEvent({
        name: "blockCreated",
        type: newBlock.type,
      });
      blocksOpsRef.current.upsertBlock(newBlock);
    }
  }, [blocksOpsRef, currentUser, userInfos, sendAnalyticsEvent]);

  useKeyAction({ key: "Enter" }, () => {
    createImage();
    updateMyPresence({ drawMode: null });
  });
  useKeyAction({ key: "Escape" }, () => {
    if (myDrawMode) {
      updateMyPresence({ drawMode: null });
    }
  });

  return (
    <DrawingStatusHolder>
      {myDrawMode ? (
        // Stop propagation so you don't draw while clicking on status
        <DrawingStatusBar onPointerDown={(e) => e.stopPropagation()}>
          <div>You are drawing</div>
          <PrimaryButton
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              createImage();
              updateMyPresence({ drawMode: null });
            }}
            style={{ marginLeft: 12 }}
            role="button"
          >
            Done
          </PrimaryButton>
        </DrawingStatusBar>
      ) : null}
    </DrawingStatusHolder>
  );
}

function DrawMode() {
  const [drawModes] = usePresence<Presence["drawMode"]>("drawMode");
  const [currentUser] = useAtom(currentUserAtom);
  const myDrawMode = currentUser ? drawModes[currentUser.id] : null;
  const othersDrawModeIds = Object.keys(drawModes).filter((id) => {
    return id !== currentUser?.id && drawModes[id] !== null;
  });

  return (
    <>
      {othersDrawModeIds.map((id) => {
        return (
          <OtherDrawMode
            key={id}
            userId={id}
            drawingInputPaths={drawModes[id] as DrawingInputPaths}
          />
        );
      })}
      {myDrawMode ? <SelfDrawMode /> : null}
      {myDrawMode && <DrawModeStatus myDrawMode={myDrawMode || null} />}
    </>
  );
}

export default DrawMode;

const PrimaryButton = styled(Button, {
  background: "$primaryTeal",
  "&:hover": {
    background: "$primaryTealHover",
  },
  "&:active": {
    background: "$primaryTealActive",
  },
});

const DrawingStatusHolder = styled("div", {
  position: "fixed",
  left: "50vw",
  bottom: "$2",
  transform: "translateX(-50%)",
  zIndex: "$drawStatus",
});

const DrawingStatusBar = styled("div", {
  borderRadius: "$2",
  background: "$paper90",
  boxShadow: "$cursor",
  paddingLeft: "$3",
  height: "$navBarHeight",
  paddingRight: "$3",
  paddingTop: "$2",
  marginBottom: "$3",
  paddingBottom: "$2",
  display: "flex",
  alignItems: "center",
  maxWidth: 240,
});

const ActiveSvgElement = styled("svg", {
  position: "absolute",
  top: 0,
  width: DEFAULT_SPACE_SIZE_PIXELS.width,
  height: DEFAULT_SPACE_SIZE_PIXELS.height,
  background: "transparent",
  zIndex: "$drawCanvas",
});
